// apps/nos/client/src/stores/createFlavorDraftStore.ts
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { AdvancedSpecsValue } from "@/pages/CreateFlavorPage/steps/AdvancedSpecs";
import { EMPTY_ANCHOR, type Anchor } from "@/pages/CreateFlavorPage/steps/anchorTypes";

export type CreateFlavorDraft = {
  draftId: string;            // uuid v4 — generated lazily on first setDraft if blank
  path: "have-target" | "discover";
  anchor: Anchor;
  advanced: AdvancedSpecsValue;
  updatedAt: number;
};

const ADVANCED_DEFAULTS: AdvancedSpecsValue = {
  basePresets: [],
  inventoryOnly: false,
  regScope: "",
  claim: null,
  application: null,
  form: null,
};

const DEFAULT_DRAFT: CreateFlavorDraft = {
  draftId: "",
  path: "have-target",
  anchor: EMPTY_ANCHOR,
  advanced: ADVANCED_DEFAULTS,
  updatedAt: 0,
};

type Store = {
  draft: CreateFlavorDraft | null;
  setDraft: (patch: Partial<CreateFlavorDraft>) => void;
  clearDraft: () => void;
};

export const useCreateFlavorDraftStore = create<Store>()(
  persist(
    (set) => ({
      draft: null,
      setDraft: (patch) =>
        set((s) => {
          const existing = s.draft ?? DEFAULT_DRAFT;
          const next: CreateFlavorDraft = {
            ...DEFAULT_DRAFT,
            ...existing,
            ...patch,
            updatedAt: Date.now(),
          };
          // Lazy-init draftId on first non-empty draft.
          if (!next.draftId) next.draftId = crypto.randomUUID();
          return { draft: next };
        }),
      clearDraft: () => set({ draft: null }),
    }),
    {
      name: "palate_create_draft",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        draft:
          s.draft && {
            ...s.draft,
            anchor: {
              ...s.draft.anchor,
              // Only persist chromas the server has confirmed uploaded.
              // 'presigning'/'uploading'/'verifying' rows can't survive a reload —
              // their browser-side File handle would be gone.
              chromas: s.draft.anchor.chromas.filter((c) => c.status === "uploaded"),
            },
          },
      }),
    },
  ),
);
